import { Loader } from '@/components/ai-elements/loader'
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
  MessageResponse,
} from '@/components/ai-elements/message'
import { Suggestion, Suggestions } from '@/components/ai-elements/suggestion'
import { Tool, ToolContent, ToolHeader } from '@/components/ai-elements/tool'
import { Button } from '@/components/ui/button'
import type {
  AssistantContentBlock,
  AssistantReaction,
  AssistantMessage as TAssistantMessage,
} from '@/hooks/use-assistant'
import { cn } from '@/lib/utils'
import type { ToolUIPart } from 'ai'
import { ArrowRight, Check, Copy, Sparkles, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useMemo, useState } from 'react'

const SUGGESTED_PROMPTS = [
  'How do I add a policy?',
  'Take me to the staff list.',
  'Where do I review controls?',
  'How do I create a new department?',
  'Show me the risk register.',
  'How can I assign a control owner?',
  'Take me to the audit logs.',
  'How do I invite a new user?',
  'Where can I see compliance reports?',
  'How do I update an existing policy?',
  'Show me overdue tasks.',
  'How do I link evidence to a control?',
  'Why can I not change a control compliance status?',
]

const SUGGESTION_COUNT = 3

const pickRandomPrompts = (pool: string[], count: number) => {
  const copy = [...pool]
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy.slice(0, count)
}

interface AssistantMessageProps {
  message: TAssistantMessage
  onDispatchToolUse: (toolUseId: string) => void
  onReact?: (messageId: string, reaction: AssistantReaction) => void
}

const statusToToolState = (status: 'pending' | 'navigated' | 'denied'): ToolUIPart['state'] => {
  if (status === 'navigated') return 'output-available'
  if (status === 'denied') return 'output-error'
  return 'input-available'
}

const ToolUseBlock = ({
  block,
  onDispatch,
}: {
  block: Extract<AssistantContentBlock, { type: 'tool_use' }>
  onDispatch: (toolUseId: string) => void
}) => {
  const why = (block.input?.why as string) ?? ''
  const path = block.resolvedPath
  const disabled = block.status !== 'pending' || !path
  const isOpenForm = block.name === 'open_add_modal'
  const entity = block.input?.entity as string | undefined
  const prefill = block.input?.prefill as Record<string, string> | undefined

  const title = isOpenForm
    ? entity
      ? `Open the add ${entity} form`
      : 'Open form'
    : block.name === 'navigate'
      ? 'Navigate'
      : block.name

  const pendingLabel = isOpenForm ? (entity ? `Open the add ${entity} form` : 'Open form') : 'Take me there'
  const deniedLabel = isOpenForm ? 'Could not open' : 'Could not navigate'

  // ToolUIPart's `type` is shaped `tool-<name>`; ai-elements derives the
  // header label from this, so format ours the same way.
  const toolType = `tool-${block.name}` as ToolUIPart['type']

  return (
    <Tool defaultOpen={block.status === 'pending'}>
      <ToolHeader title={title} type={toolType} state={statusToToolState(block.status)} />
      <ToolContent>
        <div className='flex flex-col gap-2 px-3 pb-3'>
          {why ? <p className='text-xs text-muted-foreground'>{why}</p> : null}
          {path ? (
            <p className='truncate font-mono text-[11px] text-foreground/60' title={path}>
              {path}
            </p>
          ) : null}
          {isOpenForm && prefill && Object.keys(prefill).length > 0 ? (
            <ul className='list-disc pl-4 text-[11px] text-foreground/70'>
              {Object.entries(prefill).map(([k, v]) => (
                <li key={k}>
                  <span className='font-medium'>{k}:</span> {String(v)}
                </li>
              ))}
            </ul>
          ) : null}
          <Button
            type='button'
            size='sm'
            variant={block.status === 'navigated' ? 'outline' : 'default'}
            disabled={disabled}
            onClick={() => onDispatch(block.id)}
            className='self-start'
          >
            {block.status === 'navigated' ? (
              <>
                <Check className='mr-1 h-4 w-4' />
                Opened
              </>
            ) : block.status === 'denied' ? (
              deniedLabel
            ) : (
              <>
                {pendingLabel}
                <ArrowRight className='ml-1 h-4 w-4' />
              </>
            )}
          </Button>
        </div>
      </ToolContent>
    </Tool>
  )
}

export const AssistantMessageView = ({ message, onDispatchToolUse, onReact }: AssistantMessageProps) => {
  const isUser = message.role === 'user'
  const [copied, setCopied] = useState(false)
  const reaction = message.reaction ?? null
  // Reactions are only meaningful on persisted assistant turns (server-issued
  // ids); pending replies have no row yet, and locally-generated user-side
  // ids would 404 the PATCH.
  const canReact = !isUser && !message.pending && !message.errored && Boolean(onReact)

  // Hide tool_result-only user messages - they are bookkeeping for Claude,
  // not content the human cares to see.
  const visibleBlocks = message.blocks.filter((b) => b.type !== 'tool_result')
  const copyText = visibleBlocks
    .filter((b): b is Extract<AssistantContentBlock, { type: 'text' }> => b.type === 'text')
    .map((b) => b.text.trim())
    .filter(Boolean)
    .join('\n\n')

  const handleCopy = async () => {
    if (!copyText) return
    await navigator.clipboard.writeText(copyText)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  if (visibleBlocks.length === 0 && !message.pending) return null

  if (visibleBlocks.length === 0 && message.pending)
    return (
      <Message from='assistant'>
        <MessageContent>
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader size={14} />
            Thinking…
          </div>
        </MessageContent>
      </Message>
    )

  return (
    <Message from={isUser ? 'user' : 'assistant'}>
      <MessageContent
        className={cn(message.errored && 'group-[.is-assistant]:text-destructive group-[.is-user]:bg-destructive/10')}
      >
        {visibleBlocks.map((b, i) => {
          if (b.type === 'text') {
            return isUser ? (
              <span key={i} className='whitespace-pre-wrap break-words max-w-[300px]'>
                {b.text}
              </span>
            ) : (
              <MessageResponse key={i}>{b.text}</MessageResponse>
            )
          }
          if (b.type === 'tool_use') return <ToolUseBlock key={b.id} block={b} onDispatch={onDispatchToolUse} />
          return null
        })}
      </MessageContent>
      {!isUser && !message.pending ? (
        <MessageActions className='ml-1 mt-1'>
          <MessageAction
            tooltip={copied ? 'Copied' : 'Copy'}
            label={copied ? 'Copied' : 'Copy response'}
            onClick={() => void handleCopy()}
          >
            {copied ? <Check className='h-4 w-4 ' aria-hidden /> : <Copy className='h-4 w-4' aria-hidden />}
          </MessageAction>
          <MessageAction
            tooltip={reaction === 'LIKE' ? 'Remove like' : 'Like'}
            label={reaction === 'LIKE' ? 'Liked' : 'Like response'}
            aria-pressed={reaction === 'LIKE'}
            disabled={!canReact}
            onClick={() => onReact?.(message.id, reaction === 'LIKE' ? null : 'LIKE')}
          >
            <ThumbsUp className={cn('h-4 w-4', reaction === 'LIKE' && 'fill-primary text-primary')} aria-hidden />
          </MessageAction>
          <MessageAction
            tooltip={reaction === 'DISLIKE' ? 'Remove dislike' : 'Dislike'}
            label={reaction === 'DISLIKE' ? 'Disliked' : 'Dislike response'}
            aria-pressed={reaction === 'DISLIKE'}
            disabled={!canReact}
            onClick={() => onReact?.(message.id, reaction === 'DISLIKE' ? null : 'DISLIKE')}
          >
            <ThumbsDown
              className={cn('h-4 w-4', reaction === 'DISLIKE' && 'fill-destructive text-destructive')}
              aria-hidden
            />
          </MessageAction>
        </MessageActions>
      ) : null}
    </Message>
  )
}

interface SuggestedPromptsProps {
  onSelect: (text: string) => void
  disabled?: boolean
}

export const SuggestedPrompts = ({ onSelect, disabled }: SuggestedPromptsProps) => {
  const prompts = useMemo(() => pickRandomPrompts(SUGGESTED_PROMPTS, SUGGESTION_COUNT), [])

  return (
    <div className='flex h-full flex-col items-center justify-center gap-4 text-center'>
      <div className='flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/10 text-rose-500'>
        <Sparkles className='h-6 w-6' />
      </div>
      <div className='space-y-1'>
        <h3 className='text-sm font-medium'>How can I help?</h3>
        <p className='text-xs text-muted-foreground'>Start a chat, explore ideas, or navigate faster.</p>
      </div>
      <Suggestions className='justify-center'>
        {prompts.length > 0
          ? prompts.map((prompt) => (
              <Suggestion key={prompt} suggestion={prompt} onClick={onSelect} disabled={disabled} />
            ))
          : null}
      </Suggestions>
    </div>
  )
}
